(() => {
  if (customElements.get('wc-review-strip')) return;
  const normalize = value => (value || '').replace(/\s+/g, ' ').trim();
  const dailyCount = (product, day, min, max) => {
    let hash = 2166136261;
    for (const char of `${product}:${day}`) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
    return min + (hash >>> 0) % (max - min + 1);
  };
  class ReviewStrip extends HTMLElement {
    connectedCallback() {
      this.index = 0;
      this.reviews = [];
      this.paused = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.hovered = false;
      this.focused = false;
      this.visible = true;
      this.abort = new AbortController();
      const listen = (target, event, handler) => target.addEventListener(event, handler, { signal: this.abort.signal });
      listen(this.querySelector('.wcr-pause'), 'click', () => { this.paused = !this.paused; this.button(); this.schedule(); });
      listen(this, 'mouseenter', () => { this.hovered = true; this.schedule(); });
      listen(this, 'mouseleave', () => { this.hovered = false; this.schedule(); });
      listen(this, 'focusin', () => { this.focused = true; this.schedule(); });
      listen(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); } });
      listen(document, 'visibilitychange', () => { this.sales(); this.schedule(); });
      listen(window, 'jdgm.doneSetup', () => this.queue());
      listen(document, 'jdgm.doneSetup', () => this.queue());
      this.observer = new MutationObserver(records => { if (records.some(record => !this.contains(record.target))) this.queue(); });
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.visibility = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); });
      this.visibility.observe(this);
      this.sales(); this.button(); this.sync();
    }
    disconnectedCallback() {
      this.abort.abort(); this.observer.disconnect(); this.widgetObserver?.disconnect();
      this.visibility.disconnect(); clearTimeout(this.timer); clearTimeout(this.debounce);
    }
    queue() { clearTimeout(this.debounce); this.debounce = setTimeout(() => this.sync(), 100); }
    sales() {
      const node = this.querySelector('[data-sold-count]');
      if (!node) return;
      const a = Math.max(0, Math.floor(Number(this.dataset.min) || 0));
      const b = Math.max(0, Math.floor(Number(this.dataset.max) || 0));
      node.textContent = dailyCount(this.dataset.productId, new Date().toISOString().slice(0, 10), Math.min(a, b), Math.max(a, b));
      node.parentElement.hidden = false;
    }
    sync() {
      const widget = [...document.querySelectorAll('.jdgm-review-widget')].find(node =>
        node.dataset.id === this.dataset.productId || node.dataset.productId === this.dataset.productId);
      const badge = [...document.querySelectorAll('.jdgm-prev-badge')].find(node => node.dataset.id === this.dataset.productId);
      const source = widget?.querySelector('.jdgm-rev-widg') || widget || badge;
      if (!source) return;
      const rating = Number(source.dataset.averageRating);
      const count = Number(source.dataset.numberOfReviews);
      if (Number.isFinite(rating) && Number.isFinite(count) && count > 0) {
        this.querySelector('.wcr-rating').hidden = false;
        this.querySelector('.wcr-stars-fill').style.width = `${Math.min(5, Math.max(0, rating)) * 20}%`;
        this.querySelector('.wcr-count').textContent = `${count.toLocaleString(document.documentElement.lang || 'en')} ${this.dataset.reviewsLabel || 'reviews'}`;
        this.querySelector('.wcr-rating').setAttribute('aria-label', `${rating.toFixed(1)} / 5, ${count} ${this.dataset.reviewsLabel || 'reviews'}`);
      }
      if (!widget) return;
      if (this.widget !== widget) {
        this.widget = widget;
        this.widgetObserver?.disconnect();
        this.widgetObserver = new MutationObserver(() => this.queue());
        this.widgetObserver.observe(widget, { childList: true, subtree: true, attributes: true });
        widget.addEventListener('load', () => this.queue(), { capture: true, signal: this.abort.signal });
      }
      const rows = [...widget.querySelectorAll('.jdgm-rev')].map(row => {
        const content = row.querySelector('.jdgm-rev__body')?.cloneNode(true);
        content?.querySelectorAll('.jdgm-rev__body-read-more, button').forEach(node => node.remove());
        const body = normalize(content?.textContent);
        const author = normalize(row.querySelector('.jdgm-rev__author')?.textContent);
        const photo = row.querySelector('.jdgm-rev__buyer-avatar img, .jdgm-rev__author-avatar img, .jdgm-rev__pics img');
        const src = photo?.complete && photo.naturalWidth > 0 ? photo.currentSrc || photo.src : '';
        const verified = row.hasAttribute('data-verified-buyer') ? row.dataset.verifiedBuyer === 'true' : Boolean(row.querySelector('.jdgm-rev__buyer-badge, .jdgm-rev__verified-badge'));
        const score = Number(row.querySelector('.jdgm-rev__rating')?.dataset.score);
        const rating = Number.isFinite(score) && score >= 1 && score <= 5 ? score : null;
        return { body, author, src, verified, rating };
      }).filter(row => row.body);
      const next = rows.slice(0, 10);
      const signature = JSON.stringify(next);
      if (signature === this.signature) return;
      this.signature = signature; this.reviews = next; this.index = 0;
      this.querySelector('.wcr-slider').hidden = !next.length;
      if (next.length) this.render();
      this.schedule();
    }
    render() {
      const review = this.reviews[this.index];
      this.querySelector('.wcr-quote').textContent = `“${review.body}”`;
      this.querySelector('.wcr-author').textContent = review.author;
      this.querySelector('.wcr-verified').hidden = !review.verified;
      this.querySelector('.wcr-verified-label').textContent = this.dataset.verifiedLabel || 'Verified buyer';
      const stars = this.querySelector('.wcr-review-stars');
      stars.hidden = review.rating === null;
      stars.setAttribute('aria-label', `${review.rating} / 5`);
      this.querySelector('.wcr-review-stars-fill').style.width = `${(review.rating || 0) * 20}%`;
      const photo = this.querySelector('.wcr-photo');
      photo.hidden = !review.src;
      if (review.src && photo.getAttribute('src') !== review.src) photo.src = review.src;
      const avatar = this.querySelector('.wcr-avatar');
      avatar.hidden = Boolean(review.src);
      avatar.textContent = review.author.split(' ').map(part => part[0]).slice(0, 2).join('');
    }
    button() {
      const button = this.querySelector('.wcr-pause');
      button.textContent = this.paused ? '▶' : 'Ⅱ';
      button.setAttribute('aria-label', this.paused ? button.dataset.playLabel : button.dataset.pauseLabel);
    }
    schedule() {
      clearTimeout(this.timer);
      this.querySelector('.wcr-pause').hidden = this.reviews.length < 2;
      if (this.paused || this.hovered || this.focused || !this.visible || document.hidden || this.reviews.length < 2) return;
      this.timer = setTimeout(() => { this.sales(); this.index = (this.index + 1) % this.reviews.length; this.render(); this.schedule(); }, Math.max(2, Number(this.dataset.interval) || 7) * 1000);
    }
  }
  customElements.define('wc-review-strip', ReviewStrip);
})();
